import { Fragment, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { makeStyles } from 'beam/dist/styles'
import { useAuthenticatedUser } from '../../../Permissions'

import BeamIcon from 'beam/dist/BeamIcon'
import IconButton from 'beam/dist/IconButton'
import Menu, { MenuItem } from 'beam/dist/Menu'
import { ListItemIcon, ListItemText } from 'beam/dist/List'

import { EVAL_STATE } from './evaluationForm.constants'
import {
  userCanEvaluate,
  userCanEdit,
  userCanApprove,
  userCanAcknowledge,
  userCanAppeal,
  userCanCalibrate,
} from './helpers'

const dataTestIdPrefix = 'App-Recordings-MediaPlayer-Evaluation-'

const useStyles = makeStyles(theme => ({
  menuPaper: {
    left: 'auto !important',
    right: 0,
    top: 'initial',
  },
  backItem: {
    outline: 'none',
  },
  backButton: {
    marginLeft: theme.spacing(-1),
    padding: theme.spacing(0.5),
  },
  backText: {
    marginLeft: theme.spacing(1),
  },
  hasSwitch: {
    paddingRight: theme.spacing(7),
  },
}))

const EvaluationFormMultiMenu = ({
  values,
  template,
  allAnswersValid,
  qualityEvaluationAllowed,
  calibrationEvaluationAllowed,
  calibrationView,
  onSetEvaluationView,
  onSetCalibrationView,
  onCompleteEvaluation,
  onCannotScoreEvaluation,
  onResetEvaluation,
  onSetUnansweredQuestionsToDefault,
  onEditEvaluation,
  onApproveEvaluation,
  onRejectEvaluation,
  onAcknowledgeEvaluation,
  onAppealEvaluation,
  onPrintEvaluation,
}) => {
  const { t } = useTranslation()
  const authenticatedUser = useAuthenticatedUser()
  const classes = useStyles()

  const [anchorEl, setAnchorEl] = useState(null)
  const [evalModeSubMenuOpen, setEvalModeSubMenuOpen] = useState(false)

  const handleOpen = event => {
    setAnchorEl(event.currentTarget)
    setEvalModeSubMenuOpen(false)
  }

  const openEvalModeSubMenu = () => setEvalModeSubMenuOpen(true)
  const closeEvalModeSubMenu = () => setEvalModeSubMenuOpen(false)

  const handleThenClose = handleFunc => () => {
    handleFunc && handleFunc()
    setAnchorEl(null)
  }

  const getRootMenuItems = () => {
    let items = []
    let keyIndex = 0

    const evalModeMenuItem = (
      <MenuItem key={keyIndex++} dense button divider onClick={openEvalModeSubMenu}>
        <ListItemText primary={`${t('mode')}: ${calibrationView ? t('calibration') : t('evaluation')}`} />
        <BeamIcon>ChevronRight</BeamIcon>
      </MenuItem>
    )

    const completeMenuItem = (props = {}) => (
      <MenuItem
        key={keyIndex++}
        data-testid={`${dataTestIdPrefix}Multi-Menu-Option-Complete`}
        onClick={handleThenClose(onCompleteEvaluation)}
        dense
        disabled={!allAnswersValid}
        {...props}
      >
        <ListItemText primary={t('complete')} />
      </MenuItem>

    )
    const cannotScoreMenuItem = (props = {}) => (
      <MenuItem data-testid={`${dataTestIdPrefix}Multi-Menu-Option-Cannot-Score`}
        key={keyIndex++} onClick={handleThenClose(onCannotScoreEvaluation)} dense {...props}>
        <ListItemText primary={t('cannotScore')} />
      </MenuItem>
    )
    const resetFormMenuItem = (props = {}) => (
      <MenuItem data-testid={`${dataTestIdPrefix}Multi-Menu-Option-Reset-Form`}
        key={keyIndex++} onClick={handleThenClose(onResetEvaluation)} dense {...props}>
        <ListItemText primary={t('resetForm')} />
      </MenuItem>
    )
    const setUnansweredQuestionsToDefaultMenuItem = (props = {}) => (
      <MenuItem
        data-testid={`${dataTestIdPrefix}Multi-Menu-Option-Set-Unanswered-Questions-To-Default`}
        key={keyIndex++}
        onClick={handleThenClose(onSetUnansweredQuestionsToDefault)}
        dense
        disabled={allAnswersValid}
        {...props}
      >
        <ListItemText primary={t('setUnansweredQuestionsToDefault')} />
      </MenuItem>
    )
    const editMenuItem = (props = {}) => (
      <MenuItem
        data-testid={`${dataTestIdPrefix}Multi-Menu-Option-Edit`}
        key={keyIndex++} onClick={handleThenClose(onEditEvaluation)} dense {...props}>
        <ListItemText primary={t('edit')} />
      </MenuItem>
    )
    const approveMenuItem = (props = {}) => (
      <MenuItem
        data-testid={`${dataTestIdPrefix}Multi-Menu-Option-Approve`}
        key={keyIndex++} onClick={handleThenClose(onApproveEvaluation)} dense {...props}>
        <ListItemText primary={t('approve')} />
      </MenuItem>
    )
    const rejectMenuItem = (props = {}) => (
      <MenuItem
        data-testid={`${dataTestIdPrefix}Multi-Menu-Option-Reject`}
        key={keyIndex++} onClick={handleThenClose(onRejectEvaluation)} dense {...props}>
        <ListItemText primary={t('reject')} />
      </MenuItem>
    )
    const acknowledgeEvaluationMenuItem = (props = {}) => (
      <MenuItem
        data-testid={`${dataTestIdPrefix}Multi-Menu-Option-Acknowledge-Evaluation`}
        key={keyIndex++} onClick={handleThenClose(onAcknowledgeEvaluation)} dense {...props}>
        <ListItemText primary={t('acknowledgeEvaluation')} />
      </MenuItem>
    )
    const appealEvaluationMenuItem = (props = {}) => (
      <MenuItem
        data-testid={`${dataTestIdPrefix}Multi-Menu-Option-Appeal-Evaluation`}
        key={keyIndex++} onClick={handleThenClose(onAppealEvaluation)} dense {...props}>
        <ListItemText primary={t('appealEvaluation')} />
      </MenuItem>
    )

    const printEvaluation = (props = {}) => (
      <MenuItem
        data-testid={`${dataTestIdPrefix}Multi-Menu-Option-Print-Evaluation`}
        key={keyIndex++} onClick={() => {
          // close the menu first before printing so the menu is not included in the print
          // timeout used to push onPrintEvaluation to next callstack to ensure the menu has closed
          // before executing the onPrintEvaluation callbackf
          setAnchorEl(null)
          setTimeout(onPrintEvaluation, 0)
        }}
        dense
        {...props}>
        <ListItemText primary={t('print')} />
      </MenuItem>
    )

    if (qualityEvaluationAllowed && calibrationEvaluationAllowed) {
      items = [evalModeMenuItem]
    }

    if (values !== null && values.state) {
      const canEvaluate = userCanEvaluate(values, authenticatedUser)
      const canCalibrate = userCanCalibrate(values, authenticatedUser)

      switch (values.state.stateId) {
        case EVAL_STATE.UNSCORED:
          if (canEvaluate) {
            items = [
              ...items,
              completeMenuItem(),
              cannotScoreMenuItem(),
              setUnansweredQuestionsToDefaultMenuItem(),
            ]
          } else if (canCalibrate) {
            items = [
              ...items,
              completeMenuItem(),
              cannotScoreMenuItem(),
            ]

          }
          break
        case EVAL_STATE.IN_PROGRESS:
          if (canEvaluate) {
            items = [
              ...items,
              completeMenuItem(),
              cannotScoreMenuItem(),
              resetFormMenuItem(),
              setUnansweredQuestionsToDefaultMenuItem(),
            ]
          } else if (canCalibrate) {
            items = [
              ...items,
              completeMenuItem(),
              cannotScoreMenuItem(),
            ]
          }
          break
        case EVAL_STATE.CANNOT_SCORE:
          items = [...items, resetFormMenuItem()]
          break
        case EVAL_STATE.SCORED:
          if (userCanAppeal(values, template, calibrationView, authenticatedUser)) {
            items = [...items, acknowledgeEvaluationMenuItem(), appealEvaluationMenuItem()]
          } else if (userCanAcknowledge(values, template, calibrationView, authenticatedUser)) {
            items = [...items, acknowledgeEvaluationMenuItem()]
          }
          if (canEvaluate) {
            if (userCanEdit(values, authenticatedUser)) {
              items = [...items, editMenuItem()]
            }
            items = [...items, resetFormMenuItem()]
          } else if (canCalibrate) {
            if (userCanEdit(values, authenticatedUser)) {
              items = [...items, editMenuItem()]
            }
          }
          break
        case EVAL_STATE.NEEDS_APPROVAL:
        case EVAL_STATE.NEEDS_CANNOT_SCORE_APPROVAL:
          if (userCanApprove(values, authenticatedUser)) {
            items = [...items, approveMenuItem(), rejectMenuItem()]
          }
          break
        default:
      }
    }

    return [...items, printEvaluation()]
  }

  const rootMenu = [...getRootMenuItems()]

  const evalModeSubMenu = [
    <MenuItem dense key={0} button={false} divider className={classes.backItem}>
      <IconButton className={classes.backButton} onClick={closeEvalModeSubMenu}>
        <BeamIcon>ArrowLeft</BeamIcon>
      </IconButton>
      <ListItemText className={classes.backText} primary={t('chooseAMode')} />
    </MenuItem>,
    <MenuItem key={1} dense onClick={handleThenClose(onSetEvaluationView)}>
      {!calibrationView && (
        <ListItemIcon>
          <BeamIcon>Check</BeamIcon>
        </ListItemIcon>
      )}
      <ListItemText inset={calibrationView} primary={t('evaluation')} />
    </MenuItem>,
    <MenuItem key={2} dense onClick={handleThenClose(onSetCalibrationView)}>
      {calibrationView && (
        <ListItemIcon>
          <BeamIcon>Check</BeamIcon>
        </ListItemIcon>
      )}
      <ListItemText inset={!calibrationView} primary={t('calibration')} />
    </MenuItem>,
  ]

  const getMenuItems = () => {
    if (!evalModeSubMenuOpen) {
      return rootMenu
    } else if (evalModeSubMenuOpen) {
      return evalModeSubMenu
    }
  }

  const isMenuEmpty = getMenuItems().length === 0

  return (
    <Fragment>
      <IconButton
        color="inherit"
        data-testid={`${dataTestIdPrefix}Multi-Menu-Button`}
        aria-owns={open ? 'multi-menu' : undefined}
        aria-haspopup="true"
        onClick={handleOpen}
        disabled={isMenuEmpty}>
        <BeamIcon>DotsVertical</BeamIcon>
      </IconButton>

      <Menu
        id="multi-menu"
        anchorEl={anchorEl}
        marginThreshold={0}
        open={Boolean(anchorEl)}
        onClose={handleThenClose()}
        disableScrollLock
        keepMounted
        disablePortal
      >
        {getMenuItems()}
      </Menu>
    </Fragment>
  )
}

EvaluationFormMultiMenu.propTypes = {
  values: PropTypes.object,
  template: PropTypes.object,
  allAnswersValid: PropTypes.bool.isRequired,
  qualityEvaluationAllowed: PropTypes.bool.isRequired,
  calibrationEvaluationAllowed: PropTypes.bool.isRequired,
  calibrationView: PropTypes.bool.isRequired,
  onSetEvaluationView: PropTypes.func.isRequired,
  onSetCalibrationView: PropTypes.func.isRequired,
  onCompleteEvaluation: PropTypes.func.isRequired,
  onCannotScoreEvaluation: PropTypes.func.isRequired,
  onResetEvaluation: PropTypes.func.isRequired,
  onSetUnansweredQuestionsToDefault: PropTypes.func.isRequired,
  onEditEvaluation: PropTypes.func.isRequired,
  onApproveEvaluation: PropTypes.func.isRequired,
  onRejectEvaluation: PropTypes.func.isRequired,
  onAcknowledgeEvaluation: PropTypes.func.isRequired,
  onAppealEvaluation: PropTypes.func.isRequired,
  onPrintEvaluation: PropTypes.func.isRequired,
}

EvaluationFormMultiMenu.defaultProps = {
  values: null,
  template: null,
}

export default EvaluationFormMultiMenu
